import Vue from 'vue';
import Router from 'vue-router';
import Meta from 'vue-meta';
import CryptoJS from 'crypto-js';
import store from '@/store/index';
import { authTokenCheck, metaSet, isDev } from '@/utils/helpers';
import Configuration from '../utils/configuration';
import { buildPortalBaseUrl, buildPortalUrl, newShopRedirect } from '@/utils/portalNavigation';

import Activate from '@/views/Activate';
// import CourseDetails from '@/views/pages/CourseDetails'
// import Courses from '@/views/pages/Courses'

// import Login from '@/views/Login';
// import PhoneLogin from '@/views/PhoneLogin';
// import PasswordReset from '@/views/PasswordReset';

import RoundDetails from '@/views/RoundDetails';
import RoundDetailsV3 from '@/views/RoundDetailsV3';
import Settings from '@/views/Settings';
// import SignUp from '@/views/SignUp';

import { adminRoutes } from './routes/adminRoutes';
import { shopRoutes } from './routes/shopRoutes';
import { userRoutes } from './routes/userRoutes';

//* User route Guard
import MeUserGuard from './routeGuards/MeUserGuard';
import MeUserGuardLogout from './routeGuards/MeUserGuardLogout';

//* Clubhouse route guard
import ClubhouseGuard from './routeGuards/ClubhouseGuard';
// import ClubhouseGuard2 from './routeGuards/ClubhouseGuard2';

//* Admin route guard
import AdminGuard from './routeGuards/AdminGuard';
import PartnerGuard from './routeGuards/PartnerGuard';

const VUE_APP_DISCOURSE_HASH_KEY = Configuration.value('VUE_APP_DISCOURSE_HASH_KEY');

Vue.use(Meta, {
  refreshOnceOnNavigation: true,
});

const { isNavigationFailure, NavigationFailureType } = Router;

// @ code-splitting component/page imports
//* Season page imports
const NewSeason = () => {
  return import(
    /* webpackPrefetch: true */ /* webpackChunkName: "seasonsPages" */ '@/views/NewSeason'
  );
};
const Seasons = () => {
  return import(
    /* webpackPrefetch: true */ /* webpackChunkName: "seasonsPages" */ '@/views/Seasons'
  );
};
const SeasonDetails = () => {
  return import(
    /* webpackPrefetch: true */ /* webpackChunkName: "seasonsPages" */ '@/views/SeasonDetails'
  );
};

//* Plug vue-router into Vue
Vue.use(Router);

//* Function to lazy load pages
function lazyLoad(view) {
  return () => {
    return import(
      /* webpackPrefetch: true */ /* webpackChunkName: "[request]" */ `@/views/${view}`
    );
  };
}

// let isClubHouseOwner
// let checker = async () => {
//   let club = localStorage.getItem('club')
//   let newClub = JSON.parse(club)
//   isClubHouseOwner = await !!store.dispatch('clubhouses/show', { id: newClub.uuid }, { root: true })
//   console.log('isClubHouseOwner:', isClubHouseOwner)
// }
// checker()

//* Globally dispatch clubhouse owner action
store.dispatch('users/setClub');
const isClubHouseOwner = store.getters['users/isClubHouseOwner'];

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      // redirect: '/users',
      redirect: {
        name: 'userProfile',
      },
      meta: {
        // requiresPermission: ['USER', 'LIMITEDUSER'],
      },
    },
    {
      path: '/activate',
      name: 'activate',
      component: Activate,
    },
    // @ CLUBHOUSE ROUTING ============================================
    {
      path: '/clubhouse/:clubhouseID',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "clubhousePages" */ '@/views/Clubhouse'
        );
      },
      props: true,
      meta: {
        requiresAuth: true,
        clubhouseToolbar: true,
        requiresFullAccount: true,
        clubhouseOwner: isClubHouseOwner,
      },
      children: [
        {
          path: 'dashboard',
          name: 'clubhouseDashboard',
          props: true,
          component: lazyLoad('ClubhouseDashboardPage'),
          meta: {
            requiresAuth: false,
            requiresFullAccount: true,
            title: 'Clubhouse Dashboard',
          },
        },
        {
          path: 'edit',
          name: 'clubhouseEdit',
          component: lazyLoad('ClubhouseEditPage'),
          props: true,
          beforeEnter: ClubhouseGuard,
          meta: {
            requiresAuth: true,
            clubhouseToolbar: true,
            clubhouseOwner: isClubHouseOwner,
            requiresPermission: ['ADMIN'],
            title: 'Clubhouse Edit',
          },
        },
        {
          path: 'members',
          name: 'clubhouseMembers',
          component: lazyLoad('ClubhouseMembersPage'),
          props: true,
          // beforeEnter: ClubhouseGuard,
          meta: {
            requiresAuth: true,
            clubhouseToolbar: true,
            clubhouseOwner: isClubHouseOwner,
            requiresPermission: ['ADMIN', 'EMAIL'],
            title: 'Clubhouse Members',
          },
        },
        {
          path: 'events',
          name: 'clubhouseEvents',
          component: lazyLoad('EventsUpdate'),
          // beforeEnter: ClubhouseGuard,
          props: true,
          meta: {
            header: 'Clubhouse Events',
            title: 'Clubhouse Events',
            requiresAuth: true,
            clubhouseToolbar: true,
            clubhouseOwner: isClubHouseOwner,
            requiresPermission: ['ADMIN'],
          },
        },
        {
          path: 'simulators',
          name: 'clubhouseSimulators',
          component: lazyLoad('ClubhouseSimulatorsPage'),
          props: true,
          meta: {
            requiresAuth: true,
            clubhouseToolbar: true,
            clubhouseOwner: isClubHouseOwner,
            title: 'Clubhouse Simulators',
          },
        },
        {
          path: 'seasons',
          name: 'clubhouseSeasons',
          component: Seasons,
          props: true,
          meta: {
            requiresAuth: true,
            clubhouseToolbar: true,
            clubhouseOwner: isClubHouseOwner,
            title: 'Clubhouse Seasons',
          },
        },
        {
          path: 'stats',
          name: 'clubhouseStats',
          component: lazyLoad('ClubhouseStatsPage'),
          props: true,
          // beforeEnter: ClubhouseGuard,
          meta: {
            requiresAuth: true,
            clubhouseToolbar: true,
            clubhouseOwner: isClubHouseOwner,
            // requiresPermission: ['METRICS'],
            title: 'Clubhouse Stats',
          },
        },
        {
          path: 'announcement/:cardID?',
          name: 'clubhouseAnnouncement',
          component: lazyLoad('ClubhouseAnnouncementPage'),
          props: true,
          beforeEnter: ClubhouseGuard,
          meta: {
            requiresAuth: true,
            clubhouseToolbar: true,
            clubhouseOwner: isClubHouseOwner,
            requiresPermission: ['ADMIN'],
            title: 'Clubhouse Announcement',
          },
        },
        {
          path: 'email',
          name: 'clubhouseEmail',
          component: lazyLoad('ClubhouseEmailPage'),
          props: true,
          // beforeEnter: ClubhouseGuard,
          meta: {
            requiresAuth: true,
            clubhouseToolbar: true,
            clubhouseOwner: isClubHouseOwner,
            title: 'Clubhouse Email',
          },
        },
      ],
    },
    {
      path: '/clubhouse/:clubhouseID/events/new',
      name: 'newEvent',
      component: lazyLoad('NewEvent'),
      beforeEnter: ClubhouseGuard,
      meta: {
        header: 'Clubhouse New Event',
        title: 'Clubhouse New Event',
        hidden: true,
        requiresAuth: true,
        clubhouseToolbar: true,
        clubhouseOwner: isClubHouseOwner,
        requiresPermission: ['ADMIN'],
      },
    },
    {
      path: '/clubhouse/:clubhouseID/events/:eventID/edit',
      name: 'editEvent',
      component: lazyLoad('NewEvent'),
      beforeEnter: ClubhouseGuard,
      props: true,
      meta: {
        header: 'Edit Event',
        title: 'Clubhouse Edit Event',
        requiresAuth: true,
        clubhouseToolbar: true,
        clubhouseOwner: isClubHouseOwner,
        requiresPermission: ['ADMIN'],
      },
    },
    {
      path: '/clubhouse/:clubhouseID/seasons/new',
      name: 'newSeason',
      component: NewSeason,
      beforeEnter: ClubhouseGuard,
      props: true,
      meta: {
        header: 'Create Season',
        title: 'Clubhouse New Season',
        requiresAuth: true,
        hidden: true,
        clubhouseToolbar: true,
        clubhouseOwner: isClubHouseOwner,
        requiresPermission: ['ADMIN', 'SEASONS'],
      },
    },
    {
      path: '/clubhouse/:clubhouseID/seasons/:seasonID/edit',
      name: 'editSeason',
      component: NewSeason,
      beforeEnter: ClubhouseGuard,
      props: true,
      meta: {
        header: 'Edit Season',
        title: 'Clubhouse Edit Season',
        requiresAuth: true,
        clubhouseToolbar: true,
        clubhouseOwner: isClubHouseOwner,
        requiresPermission: ['ADMIN', 'SEASONS'],
      },
    },
    {
      path: '/clubhouses',
      name: 'clubhouses',
      component: lazyLoad('Clubhouses'),
      meta: {
        requiresAuth: true,
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        title: 'Clubhouses',
      },
    },
    {
      path: '/clubhouse_unsubscribe',
      name: 'ClubhouseUnsubscribe',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "clubhousePages" */ '@/views/ClubhouseUnsubscribe'
        );
      },
      meta: {
        requiresAuth: true,
        title: 'Clubhouse Unsubscribe',
      },
    },
    {
      path: '/clubhouse/dashboard',
      name: 'clubhouseDashboardSimple',
      component: lazyLoad('ClubhouseDashboardPage'),
      // beforeEnter: ClubhouseGuard,
      meta: {
        requiresAuth: true,
        clubhouseToolbar: false,
        // clubhouseToolbar: true,
        // clubhouseOwner: isClubHouseOwner
        title: 'Clubhouse Dashboard',
      },
    },
    //* Not used currently
    // {
    //   path: '/courses',
    //   name: 'courses',
    //   component: Courses,
    //   meta: {
    //     requiresAuth: true,
    //     hidden: true,
    //     header: 'Courses',
    //     clubhouseToolbar: false,
    //   }
    // },
    // {
    //   path: '/courses/:id',
    //   name: 'courseDetails',
    //   component: CourseDetails,
    //   meta: {
    //     requiresAuth: true,
    //     hidden: true,
    //     clubhouseToolbar: false,
    //     header: 'Course Details'
    //   }
    // },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "dashboard" */ '@/views/Dashboard'
        );
      },
      props: { userCreated: false },
      redirect: '/users',
      meta: {
        requiresAuth: true,
        hidden: true,
        header: 'Dashboard',
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
      },
    },
    {
      path: '/terms/eula',
      name: 'eula',
      // component: () =>
      //   import(/* webpackPrefetch: true */ /* webpackChunkName: "eula" */ '@/views/EULA'),
      component: lazyLoad('EULA'),
      meta: {
        requiresUnauth: false,
        title: 'EULA',
      },
    },
    {
      path: '/events',
      name: 'events',
      // component: Events,
      component: lazyLoad('Events'),
      meta: {
        requiresAuth: true,
        hidden: true,
        header: 'Events',
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        title: 'Events',
      },
    },
    {
      path: '/events/:eventID',
      name: 'eventDetails',
      // component: EventDetails,
      component: lazyLoad('EventDetails'),
      meta: {
        requiresAuth: false,
        hidden: true,
        header: 'Event Details',
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        title: 'Event Details',
      },
    },
    {
      path: '/apex_events/:eventID',
      name: 'apexEventDetails',
      // component: EventDetails,
      // component: lazyLoad('ApexEventDetails2'),
      component: lazyLoad('ApexEventDetails'),
      beforeEnter: (to, from, next) => {
        const authUrl = buildPortalUrl('/auth/sync');

        if (!authUrl) {
          alert('Missing auth info — please log in again.');
          next(false);
          return;
        }

        const redirectPath = `/event/${to.params.eventID}`;
        const url = `${authUrl}&redirect=${encodeURIComponent(redirectPath)}`;

        window.open(url, '_blank');
        next(false);
      },
      meta: {
        requiresAuth: true,
        hidden: true,
        header: 'Apex Event Details',
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        title: 'Apex Event Details',
        hideAdminButtons: true,
      },
    },
    {
      path: '/season/:id',
      name: 'seasonDetails',
      component: SeasonDetails,
      props: true,
      meta: {
        requiresAuth: true,
        hidden: true,
        header: 'Season Details',
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        title: 'Season Details',
      },
    },

    // @ Begin V3 Login and noAuth pages ======================
    // TODO turn on when not checking dev env
    {
      path: '/login',
      component: () => {
        return import('@/layouts/default/NoAuthLayout.vue');
      },
      children: [
        {
          path: '',
          name: 'login',
          component: () => {
            return import('@/views/auth/LoginView.vue');
          },
          meta: {
            requiresUnauth: true,
            title: 'E6 Portal Login',
          },
        },
        {
          path: '/signup',
          name: 'signUp',
          component: () => {
            return import('@/views/auth/SignupView.vue');
          },
          meta: {
            requiresUnauth: true,
            title: 'E6 Portal Create Account',
          },
        },
        {
          path: '/reset-password',
          name: 'resetPassword',
          component: () => {
            return import('@/views/auth/ResetPasswordView.vue');
          },
          meta: {
            requiresUnauth: true,
            title: 'E6 Portal Password Reset',
          },
        },
        {
          path: '/user_verifications/:token?/reset_password',
          name: 'UserVerificationPasswordReset',
          component: () => {
            return import('@/views/auth/ResetPasswordView.vue');
          },
          meta: {
            requiresUnauth: true,
            title: 'E6 Portal Password Reset',
          },
        },
        {
          path: '/terms/tos',
          name: 'tos',
          component: () => {
            return import('@/views/general/TOSView.vue');
          },
          meta: {
            requiresUnauth: false,
            title: 'E6 Terms of Service',
          },
        },
        {
          path: '/terms/custom-tos', //* custom TOS page
          name: 'CustomTosView',
          component: () => {
            return import('@/views/general/CustomTOSView.vue');
          },
          meta: {
            requiresUnauth: false,
            title: 'Terms of Service',
          },
        },
        {
          path: '/terms/custom-privacy', //* custom privacy policy page
          name: 'CustomPrivacyView',
          component: () => {
            return import('@/views/general/CustomPrivacyView.vue');
          },
          meta: {
            requiresUnauth: false,
            title: 'Privacy Policy',
          },
        },
        {
          path: '/user-questions/:queueCode?',
          name: 'UserQuestions',
          component: () => {
            return import('@/views/general/UserQuestionForm.vue');
          },
          meta: {
            requiresUnauth: false,
            title: 'E6 Portal User Questions',
          },
        },
        {
          path: '/user-questions-start',
          name: 'UserQuestionQRView',
          component: () => {
            return import('@/views/general/UserQuestionQRView.vue');
          },
          meta: {
            requiresUnauth: false,
            title: 'E6 Portal User Questions QR',
          },
        },
        {
          path: '/user_verifications/:id?/user-questions',
          name: 'UserVerificationQuestions',
          component: () => {
            return import('@/views/general/UserQuestionForm.vue');
          },
          meta: {
            requiresUnauth: false,
            title: 'E6 Portal User Questions',
          },
        },
      ],
    },
    // @ End V3 Login and noAuth pages ===================

    //* Begin V2 ====================================================== OLD
    // {
    //   path: '/login',
    //   name: 'login',
    //   component: Login,
    //   meta: {
    //     requiresUnauth: true,
    //     title: 'E6 Portal Login',
    //   },
    // },
    // {
    //   path: '/phone-login',
    //   name: 'phoneLogin',
    //   component: PhoneLogin,
    //   props: true,
    //   meta: {
    //     requiresUnauth: true,
    //     title: 'E6 Portal Phone Login',
    //   },
    // },
    // {
    //   path: '/reset-password',
    //   name: 'resetPassword',
    //   component: PasswordReset,
    //   meta: {
    //     requiresUnauth: true,
    //     title: 'E6 Portal Password Reset',
    //   },
    // },
    // {
    //   path: '/signup',
    //   name: 'signup',
    //   component: SignUp,
    //   meta: {
    //     requiresUnauth: true,
    //     title: 'E6 Portal Create Account',
    //   },
    // },
    // {
    //   path: '/terms/tos',
    //   name: 'tos',
    //   component: () => {
    //     return import(/* webpackPrefetch: true */ /* webpackChunkName: "tos" */ '@/views/TOS');
    //   },
    //   meta: {
    //     requiresUnauth: false,
    //     title: 'E6 Terms of Service',
    //   },
    // },
    //* end V2 ======================================================

    // @ Game client login routing
    {
      path: '/game_client_login/:token?',
      component: () => {
        return import('@/layouts/default/NoAuthLayout.vue');
      },
      children: [
        {
          path: '',
          name: 'GameClientLogin',
          component: () => {
            return import('@/views/auth/GameClientLoginView');
          },
          meta: {
            requiresUnauth: false,
          },
        },
        {
          path: '/user_verifications/:id?',
          name: 'UserVerificationLogin',
          component: () => {
            return import('@/views/auth/UserVerificationLoginView');
          },
          meta: {
            requiresUnauth: false,
          },
        },
      ],
    },
    // @ Join Event Queue QR Routing
    {
      path: '/join_event_queue/:queueCode?',
      component: () => {
        return import('@/layouts/default/NoAuthLayout.vue');
      },
      children: [
        {
          path: '',
          name: 'JoinEventQueueLogin',
          component: () => {
            return import('@/views/auth/JoinQueueLoginView');
          },
          meta: {
            requiresUnauth: false,
          },
        },
        // {
        //   path: '/user_verifications/:id?',
        //   name: 'UserVerificationLogin',
        //   component: () => {
        //     return import('@/views/auth/UserVerificationLoginView');
        //   },
        //   meta: {
        //     requiresUnauth: false,
        //   },
        // },
      ],
    },
    // @ Guest Account Conversion Routing
    {
      path: '/guest_account/:gameToken/:guestUserID',
      component: () => {
        return import('@/layouts/default/NoAuthLayout.vue');
      },
      children: [
        {
          path: '',
          name: 'GuestAccountCreate',
          component: () => {
            return import('@/views/auth/GuestAccountCreateView');
          },
          meta: {
            requiresUnauth: false,
          },
        },
      ],
    },

    {
      path: '/rounds/:id/users/:userID',
      name: 'roundDetails',
      // component: isDev() ? RoundDetailsV3 : RoundDetails,
      component: RoundDetailsV3,
      meta: {
        requiresAuth: true,
        hidden: true,
        header: 'Round Details',
        title: 'Round Details',
      },
    },
    {
      path: '/settings',
      component: Settings,
      props: { subTitleKey: 'profile', subTitle: 'Edit Profile', routeKey: 'profile' },
      meta: {
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        requiresAuth: true,
        requiresFullAccount: true,
        header: 'Settings',
        title: 'Settings',
      },
    },
    {
      path: '/settings/profile',
      name: 'editProfile',
      component: Settings,
      props: { subTitleKey: 'profile', subTitle: 'Edit Profile', routeKey: 'profile' },
      meta: {
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        requiresAuth: true,
        requiresFullAccount: true,
        header: 'Settings',
        title: 'Profile',
      },
    },
    {
      path: '/settings/account',
      name: 'editAccount',
      component: Settings,
      props: { subTitleKey: 'account', subTitle: 'Edit Account', routeKey: 'account' },
      meta: {
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        requiresAuth: true,
        requiresFullAccount: true,
        header: 'Settings',
        title: 'Account',
      },
    },
    {
      path: '/settings/licenses',
      name: 'licenseSettings',
      component: Settings,
      props: { subTitleKey: 'licenses', subTitle: 'Manage Licenses', routeKey: 'licenses' },
      meta: {
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        requiresAuth: true,
        requiresFullAccount: true,
        header: 'Settings',
        title: 'Licenses',
      },
    },
    {
      path: '/settings/preferences',
      name: 'editPreferences',
      component: Settings,
      props: { subTitleKey: 'preferences', subTitle: 'Edit Preferences', routeKey: 'preferences' },
      meta: {
        clubhouseToolbar: false,
        requiresAuth: true,
        requiresFullAccount: true,
        header: 'Settings',
        title: 'Preferences',
      },
    },
    {
      //* Don't accept an id in the route. Allows anyone to view order history
      path: '/settings/history',
      name: 'orderHistory',
      component: Settings,
      props: { subTitleKey: 'history', subTitle: 'Order History', routeKey: 'history' },
      meta: {
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        requiresAuth: true,
        requiresFullAccount: true,
        header: 'History',
        title: 'OrderHistory',
      },
    },
    {
      path: '/terms/privacy-policy',
      name: 'privacy-policy',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "privacy" */ '@/views/PrivacyPolicy'
        );
      },
      meta: {
        requiresUnauth: false,
        title: 'E6 Privacy Policy',
      },
    },
    {
      path: '/info/ios-purchasing',
      name: 'IOSPurchasing',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "info" */ '@/views/IOSPurchasing'
        );
      },
      meta: {
        requiresUnauth: false,
        title: 'E6 iOS Purchasing',
      },
    },
    {
      path: '/social-share/:data?',
      name: 'SocialShare',
      // component: SocialShare,
      component: lazyLoad('SocialShare'),
      props: true,
      meta: {
        requiresUnauth: false,
      },
    },
    {
      path: '/shotview/:shotID',
      name: 'Shotview',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "rounds" */ '@/views/ShotView'
        );
      },
      meta: {
        requiresUnauth: false,
      },
    },
    {
      path: '/svd/:data',
      name: 'ShotviewData',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "rounds" */ '@/views/ShotView'
        );
      },
      meta: {
        requiresUnauth: false,
      },
    },
    // @ Bag/Club Routing ========================================
    {
      path: '/users/:id?/bag-management',
      name: 'userGolfBagManagement',
      component: lazyLoad('UserGolfBagManagement'),
      props: true,
      beforeEnter: MeUserGuardLogout,
      meta: {
        clubhouseToolbar: false,
        clubhouseOwner: isClubHouseOwner,
        requiresAuth: true,
        hidden: true,
        header: 'User Golf Bag Management',
        title: 'E6 User Golf Bag Management',
      },
    },
    //* Disabling this as it's merged into bag management now
    // {
    //   path: '/users/:id?/clubs',
    //   name: 'userGolfClubs',
    //   component: lazyLoad('UserGolfClubManagement'),
    //   props: true,
    //   beforeEnter: MeUserGuard,
    //   meta: {
    //     clubhouseToolbar: false,
    //     clubhouseOwner: isClubHouseOwner,
    //     requiresAuth: true,
    //     hidden: true,
    //     header: 'User Club Management',
    //     title: 'E6 User Club Management',
    //   },
    // },
    // @ User ROUTING ============================================
    userRoutes,
    // @ ADMIN ROUTING ============================================
    adminRoutes,
    // @ SHOP ROUTING ============================================
    shopRoutes,
    // @ Partner Routing =========================================
    {
      path: '/partner',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "partnerPages" */ '@/views/partner/Partner'
        );
      },
      props: true,
      meta: {
        requiresAuth: true,
        requiresAdmin: false,
        requiresPermission: ['PARTNER'],
        checkPartnerPermissions: true,
        checkPartnerGeneralPermissions: true,
        hidden: true,
        header: 'Dashboard',
      },
      children: [
        {
          path: '',
          name: 'Partner',
          props: true,
          component: () => {
            return import(
              /* webpackPrefetch: true */ /* webpackChunkName: "partnerPages" */ '@/views/partner/PartnerDashboard'
            );
          },
          meta: {
            requiresAuth: true,
            requiresAdmin: false,
            requiresPermission: ['PARTNER'],
            checkPartnerPermissions: true,
            checkPartnerGeneralPermissions: true,
            hidden: true,
            header: 'Dashboard',
          },
        },
        {
          path: 'dashboard',
          name: 'PartnerDashboard',
          props: true,
          component: () => {
            return import(
              /* webpackPrefetch: true */ /* webpackChunkName: "partnerPages" */ '@/views/partner/PartnerDashboard'
            );
          },
          meta: {
            requiresAuth: true,
            requiresAdmin: false,
            requiresPermission: ['PARTNER'],
            checkPartnerPermissions: true,
            checkPartnerGeneralPermissions: true,
            hidden: true,
            header: 'Dashboard',
          },
        },
        {
          path: 'productkeys/:id?',
          name: 'PartnerProductKeys',
          props: true,
          component: () => {
            return import(
              /* webpackPrefetch: true */ /* webpackChunkName: "partnerPages" */ '@/views/partner/PartnerProductKeys'
            );
          },
          meta: {
            requiresAuth: true,
            requiresAdmin: false,
            requiresPermission: ['PARTNER'],
            checkPartnerPermissions: true,
            checkPartnerGeneralPermissions: true,
            hidden: true,
            header: 'Product Keys',
            title: 'Product Keys',
          },
        },
        {
          path: 'productkeys/:id?/create',
          name: 'PartnerProductKeyCreate',
          props: true,
          component: () => {
            return import(
              /* webpackPrefetch: true */ /* webpackChunkName: "partnerPages" */ '@/views/partner/PartnerProductKeysCreate'
            );
          },
          meta: {
            requiresAuth: true,
            requiresAdmin: false,
            requiresPermission: ['PARTNER'],
            checkPartnerPermissions: true,
            checkPartnerGeneralPermissions: true,
            hidden: true,
            header: 'Create Product Keys',
            title: 'Create Product Keys',
          },
        },
        {
          path: 'productkeys/:id?/report',
          name: 'PartnerProductKeysReport',
          props: true,
          component: () => {
            return import(
              /* webpackPrefetch: true */ /* webpackChunkName: "partnerPages" */ '@/views/partner/PartnerProductKeysReport'
            );
          },
          meta: {
            requiresAuth: true,
            requiresAdmin: false,
            requiresPermission: ['PARTNER'],
            checkPartnerPermissions: true,
            checkPartnerGeneralPermissions: true,
            hidden: true,
            header: 'Product Key Report',
            title: 'Product Key Report',
          },
        },
      ],
    },
    // @ TruSimAPI Partner Routing =========================================
    {
      path: '/trusimapi',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "apipartnerPages" */ '@/views/apipartner/APIPartner'
        );
      },
      props: true,
      meta: {
        requiresAuth: true,
        requiresAdmin: false,
        requiresPermission: ['TRUSIMAPI'],
        hidden: true,
        header: 'TruSim API Dashboard',
      },
      children: [
        {
          path: '',
          name: 'APIDashboard',
          props: true,
          component: () => {
            return import(
              /* webpackPrefetch: true */ /* webpackChunkName: "apipartnerPages" */ '@/views/apipartner/APIPartnerDashboard'
            );
          },
          meta: {
            requiresAuth: true,
            requiresAdmin: false,
            requiresPermission: ['TRUSIMAPI'],
            hidden: true,
            header: 'TruSim API Dashboard',
          },
        },
        {
          path: 'distributing-software',
          name: 'APIDistributingSoftware',
          props: true,
          component: () => {
            return import(
              /* webpackPrefetch: true */ /* webpackChunkName: "apipartnerPages" */ '@/views/apipartner/APIDistributeSoftwareView.vue'
            );
          },
          meta: {
            requiresAuth: true,
            requiresAdmin: false,
            requiresPermission: ['TRUSIMAPI'],
            hidden: true,
            header: 'TruSim API Software Distribution',
          },
        },
      ],
    },
    // {
    //   path: '/shot-analytics',
    //   name: 'AdvancedAnalytics',
    //   component: () => {
    //     return import(
    //       /* webpackPrefetch: true */ /* webpackChunkName: "AdvAnalytics" */ '@/views/AdvancedAnalytics.vue'
    //     );
    //   },
    //   props: true,
    //   meta: {
    //     requiresAuth: true,
    //     hidden: true,
    //     header: 'Advanced Analytics',
    //     title: 'E6 Advanced Analytics',
    //   },
    // },
    {
      path: '/users/:id/analytics',
      name: 'Analytics',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "analytics" */ '@/views/analytics/Analytics.vue'
        );
      },
      props: true,
      beforeEnter: MeUserGuardLogout,
      meta: {
        requiresAuth: true,
        hidden: true,
        header: 'Analytics',
        title: 'E6 User Shot Analytics',
      },
    },
    // {
    //   path: '/analytics_dep',
    //   name: 'AnalyticsDep',
    //   component: () => {
    //     return import(
    //       /* webpackPrefetch: true */ /* webpackChunkName: "analytics" */ '@/views/analytics/DEPRECATED/Analytics.vue'
    //     );
    //   },
    //   props: true,
    //   meta: {
    //     requiresAuth: true,
    //     hidden: true,
    //     header: 'AnalyticsDep',
    //     title: 'E6 User Shot Analytics-DEPRECATED',
    //   },
    // },
    {
      path: '/update/e6-connect',
      name: 'e6Download',
      component: () => {
        // if (isDev()) {
        // return import(
        //   /* webpackPrefetch: true */ /* webpackChunkName: "downloads" */ '@/views/DownloadsPageCombined.vue'
        //   );
        // }
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "downloads" */ '@/views/DownloadsPageCombined.vue'
        );
      },
      props: true,
      meta: {
        requiresAuth: true,
        hidden: true,
        header: 'Software Downloads',
        title: 'E6 Connect Download',
      },
    },
    {
      path: '/connection-issue',
      name: 'connectionIssue',
      // component: ConnectionIssue,
      component: lazyLoad('ConnectionIssue'),
    },
    {
      path: '/not-authorized',
      name: 'notAuthorized',
      component: () => {
        return import(
          /* webpackPrefetch: true */ /* webpackChunkName: "adminPages" */ '@/views/NotAuthorized.vue'
        );
      },
    },
    {
      path: '*', // Catch all reroutes to the not found page
      name: 'notFound',
      // component: NotFound,
      component: lazyLoad('NotFound'),
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { selector: to.hash };
    }
    return { x: 0, y: 0 };
  },
});

// TODO remove when not checking dev env.
// if (isDev()) {
//   router.addRoute({
//     path: '/login',
//     component: () => {
//       return import('@/layouts/default/NoAuthLayout.vue');
//     },
//     children: [
//       {
//         path: '',
//         name: 'login',
//         component: () => {
//           return import('@/views/auth/LoginView.vue');
//         },
//         meta: {
//           requiresUnauth: true,
//           title: 'E6 Portal Login',
//         },
//       },
//       {
//         path: '/signup',
//         name: 'signUp',
//         component: () => {
//           return import('@/views/auth/SignupView.vue');
//         },
//         meta: {
//           requiresUnauth: true,
//           title: 'E6 Portal Create Account',
//         },
//       },
//       {
//         path: '/reset-password',
//         name: 'resetPassword',
//         component: () => {
//           return import('@/views/auth/ResetPasswordView.vue');
//         },
//         meta: {
//           requiresUnauth: true,
//           title: 'E6 Portal Password Reset',
//         },
//       },
//       {
//         path: '/user_verifications/:token?/reset_password',
//         name: 'UserVerificationPasswordReset',
//         component: () => {
//           return import('@/views/auth/ResetPasswordView.vue');
//         },
//         meta: {
//           requiresUnauth: true,
//           title: 'E6 Portal Password Reset',
//         },
//       },
//       {
//         path: '/terms/tos',
//         name: 'tos',
//         component: () => {
//           return import('@/views/general/TOSView.vue');
//         },
//         meta: {
//           requiresUnauth: false,
//           title: 'E6 Terms of Service',
//         },
//       },
//     ],
//   });
// } else {
//   router.addRoute({
//     path: '/login',
//     name: 'login',
//     component: Login,
//     meta: {
//       requiresUnauth: true,
//       title: 'E6 Portal Login',
//     },
//   });
//   router.addRoute({
//     path: '/phone-login',
//     name: 'phoneLogin',
//     component: PhoneLogin,
//     props: true,
//     meta: {
//       requiresUnauth: true,
//       title: 'E6 Portal Phone Login',
//     },
//   });
//   router.addRoute({
//     path: '/reset-password',
//     name: 'resetPassword',
//     component: PasswordReset,
//     meta: {
//       requiresUnauth: true,
//       title: 'E6 Portal Password Reset',
//     },
//   });
//   router.addRoute({
//     path: '/signup',
//     name: 'signup',
//     component: SignUp,
//     meta: {
//       requiresUnauth: true,
//       title: 'E6 Portal Create Account',
//     },
//   });
//   router.addRoute({
//     path: '/terms/tos',
//     name: 'tos',
//     component: () => {
//       return import(/* webpackPrefetch: true */ /* webpackChunkName: "tos" */ '@/views/TOS');
//     },
//     meta: {
//       requiresUnauth: false,
//       title: 'E6 Terms of Service',
//     },
//   });
// }

router.afterEach((to, from) => {
  const { name } = from;
  if (name === 'licenseSettings') {
    store.dispatch('licenses/dismissKeyMessage', false);
  }
});

router.beforeEach((to, from, next) => {
  //* Set meta details
  // metaSet(to, from, next);

  const hasRouteToken = to?.query?.token;
  // if (hasRouteToken === undefined || hasRouteToken.length === 0) {
  //   authTokenCheck(store, router, hasRouteToken);
  // }

  if (hasRouteToken && hasRouteToken.length > 0 && to.name !== 'activate') {
    // Set token and update store state immediately
    store.dispatch('authentication/setAuthenticatedFromToken', hasRouteToken);
  } else {
    authTokenCheck(store, router);
  }

  const isAuthenticated = store.getters['authentication/isAuthenticated'];
  const isAdmin = store.getters['users/isAdmin'];
  const isAccounting = store.getters['users/isAccounting'];
  const limitedUser = store.getters['users/limitedUser'];

  const keepsLegacyShop =
    store.getters['users/hasReseller'] ||
    store.getters['users/hasResellerExempt'] ||
    store.getters['users/hasOEMSeller'];

  //* Shop browsing moved to the new site; cart, checkout and receipt stay here
  //* Channel Sellers and DEV keep the legacy shop
  if (to.meta.newSiteRedirect && !keepsLegacyShop) {
    const authUrl = isAuthenticated ? buildPortalUrl('/auth/sync') : null;
    newShopRedirect.url = authUrl
      ? `${authUrl}&redirect=${encodeURIComponent('/')}`
      : buildPortalBaseUrl();
    next(false);
    return;
  }

  // let hasCorrectPermission = false;
  // hasCorrectPermission = store.state?.users?.me
  //   ? hasPermission(store.state?.users?.me, to.meta.requiresPermission)
  //   : false;

  // store.dispatch('users/setClub')
  //* Commenting out and putting in beforeRouteEnter in ClubhouseDashboardPage
  //* This worked previously, but appears to work better in component
  // isClubHouseOwner = store.getters['users/isClubHouseOwner']
  // let isClubHouseOwner2 = store.getters['users/getClubhouse']

  // ! Temp for clubhouse testing
  // let isClubHouseOwner = store.getters['authentication/isClubHouseOwner']

  if (from.name === 'admin') {
    store.dispatch('authentication/setAdminSite', false);
  }

  if (!isAuthenticated) {
    const loginPath = window.location.pathname;
    const searchQuery = window.location.search;
    if (to.meta.requiresAuth) {
      if (loginPath === '/' || loginPath === '/login') {
        next('/login');
      } else {
        next({
          name: 'login',
          query: {
            from: loginPath !== '/activate' ? loginPath : undefined,
            param: loginPath !== '/activate' ? searchQuery : undefined,
          },
        });
      }
    } else {
      next();
    }
  } else if (isAuthenticated) {
    const urlParams = new URLSearchParams(window.location.search);
    const continueURL = urlParams.get('continue');
    if (continueURL && continueURL !== '') {
      urlParams.set('token', localStorage.getItem('token'));
      const url = `${continueURL}?${urlParams.toString()}`;
      window.location.replace(url);
      return;
    }
    let sso = urlParams.get('sso');
    if (sso && sso !== '') {
      store.dispatch('users/me').then(() => {
        const userInfo = store.state.users.me;
        const keyForHash = VUE_APP_DISCOURSE_HASH_KEY;
        const hmac = CryptoJS.HmacSHA256(sso, keyForHash);
        const hmacHex = CryptoJS.enc.Hex.stringify(hmac);
        const sig = urlParams.get('sig');
        if (sig !== hmacHex) {
          alert('something wacky is happening');
        }

        sso = atob(sso);
        const params = {};

        const paramPairs = sso.split('&');
        for (const pair of paramPairs) {
          const [key, value] = pair.split('=');
          if (key) {
            params[key] = value || true;
          }
        }

        let nonce;
        let redirectURL;

        for (const [key, value] of Object.entries(params)) {
          if (key === 'return_sso_url') {
            redirectURL = decodeURIComponent(value);
          } else {
            nonce = value;
          }
        }

        const dataToSend = new URLSearchParams();
        dataToSend.set('nonce', nonce);
        dataToSend.set('email', userInfo.email);
        dataToSend.set('external_id', userInfo.id);
        dataToSend.set('username', userInfo.username);
        dataToSend.set('name', userInfo.displayName);
        dataToSend.set('avatar_url', userInfo.profileImage.imageURL);

        let ssoToSend = btoa(dataToSend.toString());
        const ssoToSendBEncode = ssoToSend;
        ssoToSend = encodeURIComponent(ssoToSend);

        const hmacToSend = CryptoJS.HmacSHA256(ssoToSendBEncode, keyForHash);
        const hmacHexToSend = CryptoJS.enc.Hex.stringify(hmacToSend);

        const url = `${redirectURL}?sso=${ssoToSend}&sig=${hmacHexToSend}`;
        window.location.replace(url);
      });
      return;
    }
    if (to.meta?.checkAdminPermissions) {
      AdminGuard(to, from, next);
    }
    if (to.meta?.checkPartnerPermissions) {
      PartnerGuard(to, from, next, to.meta?.checkPartnerGeneralPermissions);
    }

    //* New Admin check. Replaced with AdminGuard.
    // console.log('to.meta.requiresPermission:', to.meta?.requiresPermission?.length > 0);
    // if (to?.meta?.requiresPermission?.length > 0) {
    //   // console.log('here requires extra permission=============');
    //   // console.log('hasCorrectPermission:', hasCorrectPermission);
    //   if (hasCorrectPermission) {
    //     next();
    //   } else {
    //     next('/not-authorized');
    //   }
    // }

    //* Original admin check
    // if (to.meta.requiresAdmin) {
    //   console.log('here');
    //   if (isAdmin) {
    //     next();
    //   } else {
    //     store.dispatch('users/me').then(() => {
    //       isAdmin = store.getters['users/isAdmin'];
    //       isAccounting = store.getters['users/isAccounting'];
    //       if (isAdmin) {
    //         next();
    //       } else {
    //         next('/');
    //       }
    //     });
    //   }
    // }

    if (to.meta.requiresUnauth !== true) {
      // TODO Need to find way to update off from clubhouse dashboard route
      // TODO to remove toolbar

      // if (to.meta.clubhouseOwner) {
      //   // isClubHouseOwner = false
      //   isClubHouseOwner = true
      // } else {
      //   to.meta.clubhouseToolbar = false
      // }

      //* Commenting out and putting in beforeRouteEnter in ClubhouseDashboardPage
      //* This worked previously, but appears to work better in component
      // if (isClubHouseOwner && to.meta.clubhouseToolbar !== false) {
      //   to.meta.clubhouseToolbar = true
      // } else if (isClubHouseOwner && to.meta.clubhouseToolbar === false) {
      //   to.meta.clubhouseToolbar = false
      // } else if (!isClubHouseOwner) {
      //   to.meta.clubhouseToolbar = false
      // }

      // remove token from URL without adding a new history entry
      if (to.query.token !== undefined && to.query.token.length > 0 && to.name !== 'activate') {
        const newQuery = { ...to.query };
        delete newQuery.token;
        delete newQuery.timestamp;
        next({ path: to.path, query: newQuery, hash: to.hash, replace: true });
        return;
      }

      next();
      return;
    }
    if (to?.meta?.requiresUnauth && (hasRouteToken !== undefined || hasRouteToken?.length > 0)) {
      //* force logout then redirect
      store.dispatch('authentication/logout');
      store.dispatch('users/resetState');
      store.dispatch('licenses/resetLicenses');
      localStorage.removeItem('userPreferences');

      next();
    } else {
      if (to.name === 'GameClientLogin' || to.name === 'UserVerificationPasswordReset') {
        next();
        return;
      }
      next('/');
    }

    if (to.meta.requiresFullAccount) {
      if (!limitedUser) {
        next();
      } else {
        next('/');
      }
    } else {
      next();
    }
  }
});

//* Handle navigation duplication for router push (Globally)
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => {
    return err;
  });
};

router.onError((error) => {
  console.log('error:', error);
  console.log('error test:', /loading chunk \d* failed./i.test(error.message));
  console.log('error test2:', error.toString().includes('ChunkLoadError'));
  console.log('error test3:', error.toString().startsWith('ChunkLoadError'));

  if (!isNavigationFailure(error, NavigationFailureType.redirected)) {
    Promise.reject(error);
  }

  if (/^Loading( CSS)? chunk (\d)+ failed\./.test(error.message)) {
    window.location.reload(true /* skip cache */);
    return; // prevent error page blinking for user
  }

  const pattern = /Loading chunk (\d)+ failed/g;
  const patternUncaught = /Uncaught TypeError(\D)/g;
  const isChunkLoadFailed = error?.message.match(pattern);
  const isUncaughtFailed = error?.message.match(patternUncaught);
  const targetPath = router?.history?.pending?.fullPath;
  if (isChunkLoadFailed || isUncaughtFailed) {
    router.replace(targetPath);
  } else if (
    error &&
    (error?.toString().includes('ChunkLoadError') ||
      /loading chunk \d* failed./i.test(error?.message) ||
      error?.toString().startsWith('ChunkLoadError'))
  ) {
    window.location.reload();
  }
});

export default router;
